import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import {
  RotateCcw,
  ChevronUp,
  ChevronDown,
  Search,
  X,
  Filter,
  BarChart2,
  Columns,
  Download,
  Printer,
  Eye,
  AlignJustify,
} from "feather-icons-react/build/IconComponents";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { getSalesByTransType } from "../../../services/SalesApiServices";
import { setheadertoggle } from "../../../redux/toggle";
import {
  fetchCustomer,
  fetchProduct,
  fetchSettings,
} from "../../../redux/userSetting";
import { fetchUserByBranch } from "../../../redux/userServices";
import {
  printReport,
  exportExcel,
  exportPDF,
} from "../../../core/utils/reportUtils";
import { fetchBranchSettings } from "../../../redux/settingbyBranch";
import { getWareHouseList } from "../../../services/MasterApiServices";
import {
  pmHue,
  pmInitials,
} from "../../../components/master/modal/Product/pmUi";
import "../../../style/css/sales-invoice-report.css";

// ---- formatting ----------------------------------------------------------
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const num = (v) => Number(v) || 0;
const money = (v) =>
  num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const intg = (v) => num(v).toLocaleString("en-US", { maximumFractionDigits: 3 });
const pct2 = (v) => (Math.round(num(v) * 100) / 100).toFixed(2);
const short = (v) => {
  const a = Math.abs(v);
  if (a >= 1000000) return (v / 1000000).toFixed(a >= 10000000 ? 0 : 1) + "M";
  if (a >= 1000) return (v / 1000).toFixed(a >= 10000 ? 0 : 1) + "k";
  return String(Math.round(v));
};
const dmy = (s) => {
  const d = dayjs(s);
  return d.isValid() ? `${d.format("DD")} ${MON[d.month()]} ${d.format("YY")}` : "—";
};
const dm = (s) => {
  const d = dayjs(s);
  return `${d.format("DD")} ${MON[d.month()]}`;
};
const monthLabel = (k) => {
  const [y, m] = k.split("-");
  return `${MON[+m - 1]} ${y}`;
};
const iso = (d) => d.format("YYYY-MM-DD");
const daysBetween = (a, b) => dayjs(b).diff(dayjs(a), "day");
const weekKey = (s) => {
  const d = dayjs(s);
  return iso(d.subtract((d.day() + 6) % 7, "day"));
};

// ---- reference lists -----------------------------------------------------
const PAYMENTS = [
  { v: 0, t: "Cash", cls: "cash" },
  { v: 1, t: "Credit", cls: "credit" },
  { v: 2, t: "UPI", cls: "upi" },
  { v: 3, t: "Card", cls: "card" },
];

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "yday", label: "Yesterday" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "tmonth", label: "This month" },
  { id: "lmonth", label: "Last month" },
  { id: "quarter", label: "This quarter" },
  { id: "ytd", label: "Year to date" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom range" },
];

const presetRange = (id) => {
  const t = dayjs();
  switch (id) {
    case "today":
      return [iso(t), iso(t)];
    case "yday":
      return [iso(t.subtract(1, "day")), iso(t.subtract(1, "day"))];
    case "7d":
      return [iso(t.subtract(6, "day")), iso(t)];
    case "30d":
      return [iso(t.subtract(29, "day")), iso(t)];
    case "tmonth":
      return [iso(t.startOf("month")), iso(t)];
    case "lmonth": {
      const m = t.subtract(1, "month");
      return [iso(m.startOf("month")), iso(m.endOf("month"))];
    }
    case "quarter": {
      const q = Math.floor(t.month() / 3) * 3;
      return [iso(t.month(q).startOf("month")), iso(t)];
    }
    case "ytd":
      return [iso(t.startOf("year")), iso(t)];
    case "all":
      return ["", ""];
    default:
      return null;
  }
};

const CHART_LABEL = {
  "30d": "Last 30 days",
  "7d": "Last 7 days",
  "90d": "Last 90 days",
  ytd: "Year to date",
  match: "Report period",
};

const TABS = [
  { id: "detail", label: "Detailed report" },
  { id: "customer", label: "Customer" },
  { id: "item", label: "Item-wise" },
  { id: "daily", label: "Day-wise" },
];

// Titles double as export headers (reportUtils maps title -> key).
const COLUMNS = {
  detail: [
    { k: "sl", t: "Sl No" },
    { k: "date", t: "Date" },
    { k: "billNo", t: "Bill No" },
    { k: "customer", t: "Customer Name" },
    { k: "warehouse", t: "Warehouse" },
    { k: "salesman", t: "Salesman", off: true },
    { k: "payment", t: "Payment", off: true },
    { k: "total", t: "Total", num: true },
    { k: "additional", t: "Additional Amount", num: true },
    { k: "discount", t: "Discount", num: true },
    { k: "roundOff", t: "RoundOff", num: true },
    { k: "tax", t: "Tax", num: true },
    { k: "net", t: "Net Total", num: true },
  ],
  customer: [
    { k: "customer", t: "Customer Name" },
    { k: "n", t: "Invoices", int: true },
    { k: "total", t: "Total", num: true },
    { k: "discount", t: "Discount", num: true },
    { k: "tax", t: "Tax", num: true },
    { k: "net", t: "Net Total", num: true },
    { k: "avg", t: "Avg Invoice", num: true, noSum: true },
    { k: "last", t: "Last Invoice Date" },
    { k: "share", t: "Share of Net" },
  ],
  item: [
    { k: "code", t: "Item Code" },
    { k: "name", t: "Item Name" },
    { k: "unit", t: "Unit", off: true },
    { k: "n", t: "Lines", int: true },
    { k: "qty", t: "Qty", int: true },
    { k: "total", t: "Total", num: true },
    { k: "discount", t: "Discount", num: true },
    { k: "tax", t: "Tax", num: true },
    { k: "net", t: "Net Total", num: true },
    { k: "avg", t: "Avg / Unit", num: true, noSum: true, off: true },
    { k: "share", t: "Share of Net" },
  ],
  daily: [
    { k: "date", t: "Date" },
    { k: "n", t: "Invoices", int: true },
    { k: "total", t: "Total", num: true },
    { k: "discount", t: "Discount", num: true },
    { k: "tax", t: "Tax", num: true },
    { k: "net", t: "Net Total", num: true },
    { k: "avg", t: "Avg Invoice", num: true, noSum: true },
    { k: "share", t: "Share of Net" },
  ],
};

const REPORT_NAME = {
  detail: "Sales Invoice Detailed Report",
  customer: "Sales Invoice Customer",
  item: "Sales Invoice Item Wise",
  daily: "Sales Invoice Day Wise",
};

const TEXT_SORT = ["customer", "billNo", "warehouse", "salesman", "payment", "code", "name", "unit"];
const BATCH = 50;

const Caret = () => (
  <svg className="sir-caret" viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 5l3 3 3-3" />
  </svg>
);

// ---- multi-select filter -------------------------------------------------
const MultiSelect = ({ id, label, items, value, onChange, openPop, setOpenPop }) => {
  const [q, setQ] = useState("");
  const open = openPop === id;
  const searchRef = useRef(null);
  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);
  const shown = items.filter(
    (it) =>
      !q ||
      it.t.toLowerCase().includes(q.toLowerCase()) ||
      (it.c || "").toLowerCase().includes(q.toLowerCase())
  );
  const n = value.length;
  const single = n === 1 ? items.find((i) => i.v === value[0])?.t : null;
  const toggle = (v, on) =>
    onChange(on ? [...value, v] : value.filter((x) => x !== v));

  return (
    <div className={"sir-ms" + (n ? " on" : "")}>
      <button
        type="button"
        className="sir-ms-btn"
        aria-expanded={open}
        onClick={() => setOpenPop(open ? null : id)}
      >
        <span className="sir-lbl">{single || label}</span>
        {n > 0 && <span className="sir-n">{n}</span>}
        <ChevronDown />
      </button>
      {open && (
        <div className="sir-ms-pop">
          <input
            ref={searchRef}
            className="sir-ms-search"
            placeholder={`Filter ${label.toLowerCase()}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="sir-ms-list">
            {shown.length === 0 && <div className="sir-ms-none">No matches</div>}
            {shown.map((it) => (
              <label className="sir-ms-opt" key={String(it.v)}>
                <input
                  type="checkbox"
                  checked={value.includes(it.v)}
                  onChange={(e) => toggle(it.v, e.target.checked)}
                />
                <span className="sir-t">{it.t}</span>
                {it.c && <span className="sir-c">{it.c}</span>}
              </label>
            ))}
          </div>
          <div className="sir-ms-foot">
            <button type="button" onClick={() => onChange(items.map((i) => i.v))}>
              Select all
            </button>
            <button type="button" onClick={() => onChange([])}>
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SalesInvoiceReport = () => {
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { productList, customerList } = useSelector((state) => state.usersetting);
  const { UserListByBranch } = useSelector((state) => state.userservice);
  const { togglehead } = useSelector((state) => state.headerToggle);

  const [salesData, setsalesData] = useState([]);
  const [wareHouseList, setWareHouseList] = useState([]);
  const [loadMs, setLoadMs] = useState(null);

  // filters
  const initialRange = presetRange("all");
  const [preset, setPreset] = useState("all");
  const [fromDate, setFromDate] = useState(initialRange[0]);
  const [toDate, setToDate] = useState(initialRange[1]);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selProducts, setSelProducts] = useState([]);
  const [selCustomers, setSelCustomers] = useState([]);
  const [selWarehouses, setSelWarehouses] = useState([]);
  const [selSalesmen, setSelSalesmen] = useState([]);
  const [selPayments, setSelPayments] = useState([]);

  // view
  const [activeTab, setActiveTab] = useState("detail");
  const [sort, setSort] = useState({
    detail: { k: "date", d: -1 },
    customer: { k: "net", d: -1 },
    item: { k: "net", d: -1 },
    daily: { k: "date", d: -1 },
  });
  const [hiddenCols, setHiddenCols] = useState(() => {
    const h = {};
    Object.keys(COLUMNS).forEach((t) => {
      h[t] = COLUMNS[t].filter((c) => c.off).map((c) => c.k);
    });
    return h;
  });
  const [groupBy, setGroupBy] = useState("");
  const [dense, setDense] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showChart, setShowChart] = useState(true);
  const [chartRange, setChartRange] = useState("30d");
  const [bucket, setBucket] = useState("auto");
  const [openPop, setOpenPop] = useState(null);
  const [tip, setTip] = useState(null);
  const [shown, setShown] = useState(BATCH);
  const [selectedGuid, setSelectedGuid] = useState(null);
  const [drawerInv, setDrawerInv] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const searchRef = useRef(null);
  const tableWrapRef = useRef(null);
  const sentinelRef = useRef(null);
  const chartCardRef = useRef(null);
  const toastTimer = useRef(null);

  const renderTooltip = (message) => <Tooltip id="sir-tooltip">{message}</Tooltip>;
  const handletogglehead = () => dispatch(setheadertoggle(!togglehead));

  const toast = useCallback((msg) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(""), 2600);
  }, []);

  // ---- data ----
  const handlesalesData = async () => {
    const t0 = performance.now();
    try {
      const response = await getSalesByTransType(branch, 2);
      setsalesData(Array.isArray(response.data) ? response.data : []);
      setLoadMs(Math.round(performance.now() - t0));
    } catch (error) {
      console.error(
        "Failed to fetch sales invoices",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  useEffect(() => {
    dispatch(fetchProduct(branch));
    dispatch(fetchCustomer(branch));
    dispatch(fetchSettings(branch));
    dispatch(fetchBranchSettings(branch.guid));
    dispatch(fetchUserByBranch(branch));
    handlesalesData();
  }, [branch]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await getWareHouseList();
        setWareHouseList(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Failed to fetch warehouses:", err);
      }
    };
    fetchWarehouses();
  }, []);

  const warehouseMap = useMemo(
    () => Object.fromEntries(wareHouseList.map((w) => [w.guid, w.name || "Unnamed Warehouse"])),
    [wareHouseList]
  );
  const userMap = useMemo(
    () => Object.fromEntries((UserListByBranch || []).map((u) => [u.guid, u.name])),
    [UserListByBranch]
  );

  // One flat record per invoice; the keys match the column definitions.
  const invoices = useMemo(
    () =>
      salesData.map((inv) => {
        const sm = inv.salesman;
        const salesmanGuid = sm && typeof sm === "object" ? sm.guid : sm || "";
        const salesman =
          (sm && typeof sm === "object" && sm.name) || userMap[salesmanGuid] || "";
        const pay = PAYMENTS.find((p) => p.v === inv.salesPaymentType);
        return {
          guid: inv.guid,
          src: inv,
          date: inv.invdate ? dayjs(inv.invdate).format("YYYY-MM-DD") : "",
          dateSort: inv.invdate || "",
          billNo: inv.entrynumber || "",
          customer: inv.customer?.name || inv.customerName || "Unknown Customer",
          customerGuid: inv.customer?.guid,
          warehouse: warehouseMap[inv.location?.guid] || inv.location?.name || "-",
          warehouseGuid: inv.location?.guid,
          salesman,
          salesmanGuid,
          payment: pay?.t || "",
          paymentType: inv.salesPaymentType,
          total: num(inv.grossTotal),
          additional: num(inv.additionalAmt),
          discount: num(inv.discountTotal),
          roundOff: num(inv.roundOff),
          tax: num(inv.taxTotal),
          net: num(inv.netTotal),
          lines: inv.invSalesDetails || [],
        };
      }),
    [salesData, warehouseMap, userMap]
  );

  // ---- filter options ----
  const productItems = useMemo(
    () =>
      (productList || []).map((p) => ({ v: p.guid, t: p.name || "—", c: p.code || "" })),
    [productList]
  );
  const customerItems = useMemo(
    () => (customerList || []).map((c) => ({ v: c.guid, t: c.name || "—" })),
    [customerList]
  );
  const warehouseItems = useMemo(
    () => wareHouseList.map((w) => ({ v: w.guid, t: w.name || "Unnamed Warehouse" })),
    [wareHouseList]
  );
  const salesmanItems = useMemo(
    () => (UserListByBranch || []).map((u) => ({ v: u.guid, t: u.name || "—" })),
    [UserListByBranch]
  );
  const paymentItems = useMemo(() => PAYMENTS.map((p) => ({ v: p.v, t: p.t })), []);

  // ---- query ----
  const q = searchText.trim().toLowerCase();
  const matches = useCallback(
    (r, from, to) => {
      if (from && r.date < from) return false;
      if (to && r.date > to) return false;
      if (selCustomers.length && !selCustomers.includes(r.customerGuid)) return false;
      if (selWarehouses.length && !selWarehouses.includes(r.warehouseGuid)) return false;
      if (selSalesmen.length && !selSalesmen.includes(r.salesmanGuid)) return false;
      if (selPayments.length && !selPayments.includes(r.paymentType)) return false;
      if (
        selProducts.length &&
        !r.lines.some((l) => selProducts.includes(l?.product?.guid))
      )
        return false;
      if (q) {
        const hit = (s) => String(s || "").toLowerCase().includes(q);
        return (
          hit(r.billNo) ||
          hit(r.customer) ||
          hit(r.salesman) ||
          hit(r.warehouse) ||
          hit(r.date) ||
          String(r.net).includes(q) ||
          r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code))
        );
      }
      return true;
    },
    [q, selCustomers, selWarehouses, selSalesmen, selPayments, selProducts]
  );

  const rows = useMemo(
    () => invoices.filter((r) => matches(r, fromDate, toDate)),
    [invoices, matches, fromDate, toDate]
  );

  // the same window immediately before — drives the KPI deltas
  const prevRows = useMemo(() => {
    if (!fromDate || !toDate) return null;
    const len = daysBetween(fromDate, toDate) + 1;
    const pTo = iso(dayjs(fromDate).subtract(1, "day"));
    const pFrom = iso(dayjs(pTo).subtract(len - 1, "day"));
    return invoices.filter((r) => matches(r, pFrom, pTo));
  }, [invoices, matches, fromDate, toDate]);

  // ---- aggregates ----
  const totalsOf = (list) => {
    const t = { n: list.length, total: 0, discount: 0, tax: 0, net: 0 };
    list.forEach((r) => {
      t.total += r.total;
      t.discount += r.discount;
      t.tax += r.tax;
      t.net += r.net;
    });
    return t;
  };

  const byCustomer = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = r.customerGuid || r.customer;
      if (!m[k])
        m[k] = { key: k, customer: r.customer, n: 0, total: 0, discount: 0, tax: 0, net: 0, last: "" };
      const g = m[k];
      g.n++;
      g.total += r.total;
      g.discount += r.discount;
      g.tax += r.tax;
      g.net += r.net;
      if (r.date > g.last) g.last = r.date;
    });
    return Object.values(m).map((g) => ({ ...g, avg: g.n ? g.net / g.n : 0 }));
  }, [rows]);

  const byItem = useMemo(() => {
    const m = {};
    rows.forEach((r) =>
      r.lines.forEach((l) => {
        const pg = l?.product?.guid;
        if (selProducts.length && !selProducts.includes(pg)) return;
        const k = pg || l?.product?.name || "unknown";
        if (!m[k])
          m[k] = {
            key: k,
            code: l?.product?.code || "—",
            name: l?.product?.name || "Unknown Item",
            unit: l?.unit?.name || "",
            n: 0, qty: 0, total: 0, discount: 0, tax: 0, net: 0,
          };
        const g = m[k];
        const qty = num(l.quantity);
        g.n++;
        g.qty += qty;
        g.total += num(l.grossAmount ?? l.amount ?? qty * num(l.price));
        g.discount += num(l.discAmt);
        g.tax += num(l.taxamnt);
        g.net += num(l.netAmt);
      })
    );
    return Object.values(m).map((g) => ({ ...g, avg: g.qty ? g.net / g.qty : 0 }));
  }, [rows, selProducts]);

  const byDay = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!m[r.date]) m[r.date] = { key: r.date, date: r.date, n: 0, total: 0, discount: 0, tax: 0, net: 0 };
      const g = m[r.date];
      g.n++;
      g.total += r.total;
      g.discount += r.discount;
      g.tax += r.tax;
      g.net += r.net;
    });
    return Object.values(m).map((g) => ({ ...g, avg: g.n ? g.net / g.n : 0 }));
  }, [rows]);

  const tabCounts = {
    detail: rows.length,
    customer: byCustomer.length,
    item: byItem.length,
    daily: byDay.length,
  };

  // ---- table data ----
  const cols = COLUMNS[activeTab].filter((c) => !hiddenCols[activeTab].includes(c.k));
  const grouping = activeTab === "detail" && !!groupBy;
  const groupOf = (r) =>
    groupBy === "month" ? monthLabel(r.date.slice(0, 7)) : r[groupBy] || "—";

  const list = useMemo(() => {
    const base =
      activeTab === "customer"
        ? byCustomer
        : activeTab === "item"
        ? byItem
        : activeTab === "daily"
        ? byDay
        : rows;
    const { k, d } = sort[activeTab];
    const key = k === "date" ? "dateSort" : k;
    const out = base.map((r) => ({ ...r }));
    out.sort((a, b) => {
      const av = a[key] ?? a[k];
      const bv = b[key] ?? b[k];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * d;
      return String(av || "").localeCompare(String(bv || "")) * d;
    });
    if (grouping)
      out.sort((a, b) => String(groupOf(a)).localeCompare(String(groupOf(b))));
    if (activeTab === "detail") out.forEach((r, i) => (r.sl = i + 1));
    return out;
  }, [activeTab, rows, byCustomer, byItem, byDay, sort, grouping, groupBy]);

  const grandNet = list.reduce((a, r) => a + (r.net || 0), 0);

  const footTotals = useMemo(() => {
    const acc = {};
    cols.forEach((c) => {
      if ((c.num || c.int) && !c.noSum) acc[c.k] = list.reduce((a, r) => a + num(r[c.k]), 0);
    });
    return acc;
  }, [list, cols]);

  const groupInfo = useMemo(() => {
    if (!grouping) return {};
    const info = {};
    list.forEach((r) => {
      const g = groupOf(r);
      if (!info[g]) {
        info[g] = { n: 0 };
        cols.forEach((c) => c.num && (info[g][c.k] = 0));
      }
      info[g].n++;
      cols.forEach((c) => c.num && (info[g][c.k] += num(r[c.k])));
    });
    return info;
  }, [list, grouping, cols]);

  // reset the loaded batch whenever the listing changes
  useEffect(() => {
    setShown(BATCH);
    if (tableWrapRef.current) tableWrapRef.current.scrollTop = 0;
  }, [list]);

  useEffect(() => {
    const root = tableWrapRef.current;
    const target = sentinelRef.current;
    if (!root || !target || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setShown((s) => Math.min(s + BATCH, list.length));
      },
      { root, rootMargin: "260px" }
    );
    io.observe(target);
    return () => io.disconnect();
    // re-observing after each batch re-checks a sentinel that is still in view
  }, [list, shown]);

  // ---- trend ----
  const chartWindow = () => {
    const t = dayjs();
    if (chartRange === "match") {
      if (fromDate && toDate) return [fromDate, toDate];
      const dates = invoices.map((r) => r.date).filter(Boolean).sort();
      return dates.length ? [dates[0], dates[dates.length - 1]] : [iso(t), iso(t)];
    }
    if (chartRange === "ytd") return [iso(t.startOf("year")), iso(t)];
    const n = chartRange === "7d" ? 7 : chartRange === "90d" ? 90 : 30;
    return [iso(t.subtract(n - 1, "day")), iso(t)];
  };
  const win = chartWindow();
  const [winFrom, winTo] = win;
  const bucketMode = (() => {
    if (bucket !== "auto") return bucket;
    const days = daysBetween(win[0], win[1]) + 1;
    return days <= 62 ? "day" : days <= 400 ? "week" : "month";
  })();

  const chart = useMemo(() => {
    const m = {};
    invoices.forEach((r) => {
      if (!matches(r, winFrom, winTo)) return;
      const k =
        bucketMode === "day" ? r.date : bucketMode === "week" ? weekKey(r.date) : r.date.slice(0, 7);
      if (!m[k]) m[k] = { key: k, net: 0, n: 0 };
      m[k].net += r.net;
      m[k].n++;
    });
    const data = Object.keys(m).sort().map((k) => m[k]);
    const max = data.reduce((a, b) => Math.max(a, b.net), 0);
    const nice = Math.pow(10, Math.floor(Math.log10(max || 1)));
    const top = Math.ceil((max || 1) / (nice / 2)) * (nice / 2) || 1;
    return { data, max, top };
  }, [invoices, matches, winFrom, winTo, bucketMode]);

  const xStep = Math.max(1, Math.ceil(chart.data.length / 12));
  const bucketLabel = (k, long) =>
    bucketMode === "month" ? monthLabel(k) : bucketMode === "week" && long ? `Week of ${dmy(k)}` : long ? dmy(k) : dm(k);

  const onBarEnter = (e, i) => {
    const card = chartCardRef.current?.getBoundingClientRect();
    const b = e.currentTarget.getBoundingClientRect();
    if (!card) return;
    setTip({ i, left: b.left - card.left + b.width / 2, top: b.top - card.top - 8 });
  };

  // ---- KPIs ----
  const kt = totalsOf(rows);
  const kp = prevRows ? totalsOf(prevRows) : null;
  const delta = (now, before) =>
    kp && before ? ((now - before) / Math.abs(before)) * 100 : null;

  const Kpi = ({ k, v, d, hero, invert }) => {
    let arrow = null;
    if (d !== null && isFinite(d)) {
      const good = invert ? d < 0 : d >= 0;
      arrow = (
        <div className={"sir-kd " + (good ? "up" : "down")}>
          <b>
            {d >= 0 ? "▲ " : "▼ "}
            {pct2(Math.abs(d))}%
          </b>{" "}
          vs previous period
        </div>
      );
    }
    return (
      <div className={"sir-card sir-kpi" + (hero ? " hero" : "")}>
        <div className="sir-kk">{k}</div>
        <div className="sir-kv">{v}</div>
        {arrow}
      </div>
    );
  };

  // ---- chips ----
  const labelFor = (items, arr) =>
    arr.length === 1 ? items.find((i) => i.v === arr[0])?.t || "1 selected" : `${arr.length} selected`;
  const chips = [];
  if (searchText) chips.push({ label: "Search", value: searchText, clear: () => { setSearchInput(""); setSearchText(""); } });
  [
    ["Products", productItems, selProducts, setSelProducts],
    ["Customers", customerItems, selCustomers, setSelCustomers],
    ["Warehouses", warehouseItems, selWarehouses, setSelWarehouses],
    ["Salesmen", salesmanItems, selSalesmen, setSelSalesmen],
    ["Payment", paymentItems, selPayments, setSelPayments],
  ].forEach(([label, items, arr, set]) => {
    if (arr.length) chips.push({ label, value: labelFor(items, arr), clear: () => set([]) });
  });

  // ---- handlers ----
  const applyPreset = (id) => {
    setPreset(id);
    const r = presetRange(id);
    if (r) {
      setFromDate(r[0]);
      setToDate(r[1]);
    }
  };

  const handleReset = () => {
    setSearchInput("");
    setSearchText("");
    setSelProducts([]);
    setSelCustomers([]);
    setSelWarehouses([]);
    setSelSalesmen([]);
    setSelPayments([]);
    setGroupBy("");
    applyPreset("all");
  };

  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput), 140);
    return () => clearTimeout(t);
  }, [searchInput]);

  const onSort = (k) =>
    setSort((s) => {
      const cur = s[activeTab];
      const next =
        cur.k === k
          ? { k, d: -cur.d }
          : { k, d: TEXT_SORT.includes(k) || k === "date" ? 1 : -1 };
      return { ...s, [activeTab]: next };
    });

  const toggleCol = (k, on) =>
    setHiddenCols((h) => ({
      ...h,
      [activeTab]: on ? h[activeTab].filter((x) => x !== k) : [...h[activeTab], k],
    }));

  const openInvoice = (r) => {
    setSelectedGuid(r.guid);
    setDrawerInv(r);
    requestAnimationFrame(() => requestAnimationFrame(() => setDrawerOpen(true)));
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setDrawerInv(null), 200);
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest(".sir-ms") && !e.target.closest(".sir-popwrap")) setOpenPop(null);
    };
    const onKey = (e) => {
      if (e.key === "F3") {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      } else if (e.key === "Escape") {
        if (drawerOpen) closeDrawer();
        else setOpenPop(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  // ---- export ----
  const getActiveTabData = () => {
    const columns = cols
      .filter((c) => c.k !== "share")
      .map((c) => ({ title: c.t, dataIndex: c.k }));
    const fieldMapping = Object.fromEntries(columns.map((c) => [c.title, c.dataIndex]));
    return { data: list, columns, Rname: REPORT_NAME[activeTab], fieldMapping };
  };

  const downloadCSV = () => {
    const exp = cols.filter((c) => c.k !== "share" && c.k !== "sl");
    const cell = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [exp.map((c) => cell(c.t)).join(",")]
      .concat(
        list.map((r) =>
          exp.map((c) => cell(c.num ? pct2(r[c.k]) : r[c.k])).join(",")
        )
      )
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${REPORT_NAME[activeTab]}${fromDate ? "_" + fromDate + "_" + toDate : ""}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    toast(`CSV downloaded · ${list.length} rows`);
  };

  // ---- rendering helpers ----
  const payPill = (r) =>
    r.payment ? (
      <span className={"sir-pill " + (PAYMENTS.find((p) => p.v === r.paymentType)?.cls || "")}>
        <i />
        {r.payment}
      </span>
    ) : (
      "—"
    );

  const renderCell = (r, c) => {
    switch (c.k) {
      case "sl":
        return <td key={c.k} className="sir-idx">{r.sl}</td>;
      case "date":
      case "last":
        return <td key={c.k} className="sir-date">{r[c.k] ? dmy(r[c.k]) : "—"}</td>;
      case "billNo":
      case "code":
        return <td key={c.k} className="sir-bill">{r[c.k]}</td>;
      case "customer":
        return (
          <td key={c.k}>
            <div className="sir-cust">
              <span className="sir-av" style={{ "--h": pmHue(r.customer) }}>
                {pmInitials(r.customer)}
              </span>
              <span className="sir-t">{r.customer}</span>
            </div>
          </td>
        );
      case "payment":
        return <td key={c.k}>{payPill(r)}</td>;
      case "share": {
        const p = grandNet > 0 ? (r.net / grandNet) * 100 : 0;
        return (
          <td key={c.k}>
            <div className="sir-share">
              <span className="sir-sb">
                <i style={{ width: `${Math.min(100, Math.max(0, p))}%` }} />
              </span>
              <span className="sir-st">{pct2(p)}%</span>
            </div>
          </td>
        );
      }
      default:
        if (c.int) return <td key={c.k} className="sir-num">{intg(r[c.k])}</td>;
        if (c.num) {
          const v = num(r[c.k]);
          const cls = c.k === "net" ? "sir-net" : "sir-num" + (v === 0 ? " muted" : "");
          return <td key={c.k} className={cls}>{money(v)}</td>;
        }
        return <td key={c.k}>{r[c.k] || "—"}</td>;
    }
  };

  const leadCount = (() => {
    let lead = 0;
    while (lead < cols.length && !cols[lead].num && !cols[lead].int) lead++;
    return Math.max(1, lead);
  })();

  const bodyRows = [];
  let lastGroup = null;
  list.slice(0, shown).forEach((r, i) => {
    if (grouping) {
      const g = groupOf(r);
      if (g !== lastGroup) {
        const info = groupInfo[g] || {};
        bodyRows.push(
          <tr className="sir-grp" key={`g-${g}-${i}`}>
            <td colSpan={leadCount}>
              <div className="sir-gname">
                {g}
                <span className="sir-gn">
                  {info.n} invoice{info.n === 1 ? "" : "s"}
                </span>
              </div>
            </td>
            {cols.slice(leadCount).map((c) =>
              c.num ? (
                <td key={c.k} className="sir-num">{money(info[c.k])}</td>
              ) : (
                <td key={c.k} />
              )
            )}
            <td />
          </tr>
        );
        lastGroup = g;
      }
    }
    const isDetail = activeTab === "detail";
    bodyRows.push(
      <tr
        key={r.guid || r.key || i}
        className={isDetail && r.guid === selectedGuid ? "sel" : ""}
        tabIndex={isDetail ? 0 : undefined}
        onClick={isDetail ? () => openInvoice(r) : undefined}
        onKeyDown={isDetail ? (e) => e.key === "Enter" && openInvoice(r) : undefined}
      >
        {cols.map((c) => renderCell(r, c))}
        {isDetail && (
          <td>
            <div className="sir-rowacts">
              <button type="button" className="sir-rowbtn" title="View invoice" aria-label="View invoice">
                <Eye />
              </button>
            </div>
          </td>
        )}
      </tr>
    );
  });

  const rangeText =
    fromDate && toDate ? `${dmy(fromDate)} → ${dmy(toDate)}` : "All dates";
  const left = list.length - Math.min(shown, list.length);
  const curSort = sort[activeTab];
  const tipData = tip ? chart.data[tip.i] : null;

  const di = drawerInv;
  const drawerLines = di?.lines || [];

  return (
    <div className={"sir-screen" + (dense ? " sir-dense" : "")}>
      <div className="page-wrapper sir-wrapper">
        <div className="content sir-page">
          {/* ============ REPORT HEADER ============ */}
          <div className="sir-rhead">
            <div className="sir-rtitle">
              <h2>
                Sales Invoice Report
                <span className="sir-range">{rangeText}</span>
              </h2>
              <p>
                Every invoice raised in the selected period · <b>{intg(rows.length)}</b> invoices
              </p>
            </div>
            <div className="sir-rtools">
              <div className="sir-popwrap">
                <button
                  type="button"
                  className="sir-iconbtn"
                  aria-expanded={openPop === "cols"}
                  onClick={() => setOpenPop(openPop === "cols" ? null : "cols")}
                  title="Choose columns"
                >
                  <Columns />
                  Columns
                </button>
                {openPop === "cols" && (
                  <div className="sir-pop right">
                    <div className="sir-pop-h">Columns on this tab</div>
                    {COLUMNS[activeTab].map((c) => (
                      <label className="sir-pop-opt" key={c.k}>
                        <input
                          type="checkbox"
                          checked={!hiddenCols[activeTab].includes(c.k)}
                          onChange={(e) => toggleCol(c.k, e.target.checked)}
                        />
                        {c.t}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="sir-iconbtn"
                aria-pressed={showChart}
                onClick={() => setShowChart((v) => !v)}
                title="Show or hide the trend"
              >
                <BarChart2 />
                Trend
              </button>
              <button
                type="button"
                className="sir-iconbtn"
                aria-pressed={showFilters}
                onClick={() => setShowFilters((v) => !v)}
                title="Show or hide filters"
              >
                <Filter />
                Filters
              </button>
              <span className="sir-sepbar" />
              <OverlayTrigger placement="top" overlay={renderTooltip("Refresh")}>
                <button
                  type="button"
                  className="sir-iconbtn"
                  aria-label="Refresh"
                  onClick={() => {
                    handlesalesData();
                    toast("Data reloaded");
                  }}
                >
                  <RotateCcw />
                </button>
              </OverlayTrigger>
              <button type="button" className="sir-iconbtn" onClick={downloadCSV} title="Download CSV">
                <Download />
                CSV
              </button>
              <OverlayTrigger placement="top" overlay={renderTooltip("Excel")}>
                <Link
                  to="#"
                  className="sir-iconbtn"
                  onClick={() => exportExcel(getActiveTabData, "Sales invoice")}
                >
                  <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                  Excel
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("PDF")}>
                <Link
                  to="#"
                  className="sir-iconbtn"
                  onClick={() => exportPDF(getActiveTabData, "Sales invoice")}
                >
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  PDF
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("Print")}>
                <Link
                  to="#"
                  className="sir-iconbtn"
                  aria-label="Print"
                  onClick={() => printReport(getActiveTabData, "Sales invoice")}
                >
                  <Printer />
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("Collapse")}>
                <Link
                  to="#"
                  aria-label="Collapse"
                  id="collapse-header"
                  className={"sir-iconbtn" + (togglehead ? " active" : "")}
                  onClick={handletogglehead}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </div>
          </div>

          {/* ============ FILTERS ============ */}
          {showFilters && (
            <section className="sir-card sir-filters">
              <div className="sir-frow top">
                <div className="sir-fld sir-searchfld">
                  <label htmlFor="sir-q">Search</label>
                  <Search className="sir-sicon" />
                  <input
                    id="sir-q"
                    ref={searchRef}
                    autoComplete="off"
                    placeholder="Bill no, customer, item, salesman…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && setSearchText(searchInput)}
                  />
                  {searchInput && (
                    <button
                      type="button"
                      className="sir-clr"
                      aria-label="Clear search"
                      onClick={() => {
                        setSearchInput("");
                        setSearchText("");
                        searchRef.current?.focus();
                      }}
                    >
                      <X />
                    </button>
                  )}
                  <span className="sir-kbd">F3</span>
                </div>
                <div className="sir-fld sel">
                  <label htmlFor="sir-preset">Period</label>
                  <select
                    id="sir-preset"
                    value={preset}
                    onChange={(e) =>
                      e.target.value === "custom" ? setPreset("custom") : applyPreset(e.target.value)
                    }
                  >
                    {PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <Caret />
                </div>
                <div className="sir-fld">
                  <label htmlFor="sir-from">From date</label>
                  <input
                    id="sir-from"
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      if (toDate && e.target.value && e.target.value > toDate) setToDate(e.target.value);
                      setPreset("custom");
                    }}
                  />
                </div>
                <div className="sir-fld">
                  <label htmlFor="sir-to">To date</label>
                  <input
                    id="sir-to"
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setPreset("custom");
                    }}
                  />
                </div>
                <div className="sir-fbtns">
                  <button
                    type="button"
                    className="sir-btn sir-btn-primary"
                    onClick={() => {
                      setSearchText(searchInput);
                      toast("Report refreshed");
                    }}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="sir-btn sir-btn-ghost"
                    onClick={() => {
                      handleReset();
                      toast("Filters cleared");
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="sir-presets">
                {PRESETS.filter((p) => p.id !== "custom").map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className="sir-pchip"
                    aria-pressed={preset === p.id}
                    onClick={() => applyPreset(p.id)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="sir-frow dims">
                <MultiSelect id="products" label="Products" items={productItems} value={selProducts} onChange={setSelProducts} openPop={openPop} setOpenPop={setOpenPop} />
                <MultiSelect id="customers" label="Customers" items={customerItems} value={selCustomers} onChange={setSelCustomers} openPop={openPop} setOpenPop={setOpenPop} />
                <MultiSelect id="warehouses" label="Warehouses" items={warehouseItems} value={selWarehouses} onChange={setSelWarehouses} openPop={openPop} setOpenPop={setOpenPop} />
                <MultiSelect id="salesmen" label="Salesmen" items={salesmanItems} value={selSalesmen} onChange={setSelSalesmen} openPop={openPop} setOpenPop={setOpenPop} />
                <MultiSelect id="payments" label="Payment" items={paymentItems} value={selPayments} onChange={setSelPayments} openPop={openPop} setOpenPop={setOpenPop} />
              </div>
            </section>
          )}

          {chips.length > 0 && (
            <div className="sir-chiprow">
              <span className="sir-ttl">Filters</span>
              {chips.map((c) => (
                <span className="sir-fchip" key={c.label}>
                  {c.label} <b>{c.value}</b>
                  <button type="button" aria-label="Remove filter" onClick={c.clear}>
                    <X />
                  </button>
                </span>
              ))}
              <button type="button" className="sir-clearall" onClick={handleReset}>
                Clear all
              </button>
            </div>
          )}

          {/* ============ KPI ============ */}
          <div className="sir-kpis">
            <Kpi k="Invoices" v={intg(kt.n)} d={delta(kt.n, kp?.n)} />
            <Kpi k="Gross sales" v={money(kt.total)} d={delta(kt.total, kp?.total)} />
            <Kpi k="Discount" v={money(kt.discount)} d={delta(kt.discount, kp?.discount)} invert />
            <Kpi k="VAT collected" v={money(kt.tax)} d={delta(kt.tax, kp?.tax)} />
            <Kpi k="Net sales" v={money(kt.net)} d={delta(kt.net, kp?.net)} hero />
            <Kpi
              k="Average invoice"
              v={money(kt.n ? kt.net / kt.n : 0)}
              d={delta(kt.n ? kt.net / kt.n : 0, kp && kp.n ? kp.net / kp.n : 0)}
            />
          </div>

          {/* ============ TREND ============ */}
          {showChart && (
            <section className="sir-card sir-chartcard" ref={chartCardRef}>
              <div className="sir-charthead">
                <span className="sir-ct">Net sales over time</span>
                <span className="sir-cs">
                  {CHART_LABEL[chartRange]} · {dmy(win[0])} → {dmy(win[1])}
                  {chart.data.length ? ` · peak ${money(chart.max)}` : " · nothing to plot"}
                </span>
                <div className="sir-cr">
                  <div className={"sir-minisel" + (chartRange !== "match" ? " on" : "")}>
                    <select value={chartRange} onChange={(e) => setChartRange(e.target.value)} aria-label="Trend period">
                      <option value="30d">Last 30 days</option>
                      <option value="7d">Last 7 days</option>
                      <option value="90d">Last 90 days</option>
                      <option value="ytd">Year to date</option>
                      <option value="match">Match report period</option>
                    </select>
                    <Caret />
                  </div>
                  <div className="sir-minisel">
                    <select value={bucket} onChange={(e) => setBucket(e.target.value)} aria-label="Bucket">
                      <option value="auto">Auto</option>
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                      <option value="month">Monthly</option>
                    </select>
                    <Caret />
                  </div>
                </div>
              </div>
              <div className="sir-plot">
                <div className="sir-yaxis">
                  {[1, 0.5, 0].map((f) => (
                    <span key={f} style={{ top: `${(1 - f) * 100}%` }}>
                      {short(chart.top * f)}
                    </span>
                  ))}
                </div>
                <div className="sir-plotarea">
                  <div className="sir-gridlines">
                    {[1, 0.5, 0].map((f) => (
                      <i key={f} className={f === 0 ? "zero" : ""} style={{ top: `${(1 - f) * 100}%` }} />
                    ))}
                  </div>
                  <div className="sir-bars" onMouseLeave={() => setTip(null)}>
                    {chart.data.map((d, i) => (
                      <div
                        key={d.key}
                        className={"sir-bar" + (tip && tip.i !== i ? " dim" : "")}
                        style={{ height: `${Math.max(2, (d.net / chart.top) * 100)}%` }}
                        onMouseEnter={(e) => onBarEnter(e, i)}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="sir-xaxis">
                {chart.data.map((d, i) => (
                  <span key={d.key}>{i % xStep === 0 ? bucketLabel(d.key, false) : ""}</span>
                ))}
              </div>
              {tipData && (
                <div className="sir-ctip" style={{ left: tip.left, top: tip.top }}>
                  <div className="sir-ctd">{bucketLabel(tipData.key, true)}</div>
                  <div className="sir-ctm">
                    {money(tipData.net)}
                    <small>
                      {tipData.n} invoice{tipData.n === 1 ? "" : "s"}
                    </small>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ============ TABLE ============ */}
          <section className="sir-card sir-tablecard">
            <nav className="sir-tabs" role="tablist">
              {TABS.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  className="sir-tab"
                  role="tab"
                  aria-selected={activeTab === t.id}
                  onClick={() => {
                    setActiveTab(t.id);
                    setOpenPop(null);
                  }}
                >
                  {t.label} <span className="sir-n">{intg(tabCounts[t.id])}</span>
                </button>
              ))}
            </nav>

            <div className="sir-tbar">
              {activeTab === "detail" && (
                <>
                  <span className="sir-lbl">Group by</span>
                  <div className={"sir-minisel" + (groupBy ? " on" : "")}>
                    <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} aria-label="Group by">
                      <option value="">None</option>
                      <option value="customer">Customer</option>
                      <option value="warehouse">Warehouse</option>
                      <option value="salesman">Salesman</option>
                      <option value="payment">Payment</option>
                      <option value="month">Month</option>
                    </select>
                    <Caret />
                  </div>
                </>
              )}
              <button
                type="button"
                className="sir-iconbtn"
                aria-pressed={dense}
                onClick={() => setDense((v) => !v)}
                title="Compact rows"
              >
                <AlignJustify />
                Compact
              </button>
              <div className="sir-right">
                <span className="sir-count">
                  <b>{intg(list.length)}</b> rows{grouping ? ` · grouped by ${groupBy}` : ""}
                </span>
              </div>
            </div>

            <div className="sir-tablewrap" ref={tableWrapRef}>
              <table className="sir-rpt">
                <thead>
                  <tr>
                    {cols.map((c) => (
                      <th key={c.k} className={c.num || c.int || c.k === "share" ? "r" : ""} style={c.k === "sl" ? { width: Math.max(40, 22 + String(list.length).length * 9) } : undefined}>
                        <button type="button" onClick={() => onSort(c.k)}>
                          {c.t}
                          {curSort.k === c.k && <span className="sir-ar">{curSort.d > 0 ? "▲" : "▼"}</span>}
                        </button>
                      </th>
                    ))}
                    {activeTab === "detail" && <th style={{ width: 48 }} />}
                  </tr>
                </thead>
                <tbody>{bodyRows}</tbody>
                {list.length > 0 && (
                  <tfoot>
                    <tr>
                      {cols.map((c, i) => {
                        if ((c.num || c.int) && !c.noSum) {
                          return (
                            <td key={c.k} className={"r" + (c.k === "net" ? " sir-net" : "")}>
                              {c.int ? intg(footTotals[c.k]) : money(footTotals[c.k])}
                            </td>
                          );
                        }
                        if (i === 0) {
                          return (
                            <td key={c.k} className="sir-flbl">
                              Total · {intg(list.length)} rows
                            </td>
                          );
                        }
                        return <td key={c.k} />;
                      })}
                      {activeTab === "detail" && <td />}
                    </tr>
                  </tfoot>
                )}
              </table>
              <div className="sir-sentinel" ref={sentinelRef} />
              {list.length === 0 && (
                <div className="sir-empty">
                  <div className="sir-big">No invoices in this selection</div>
                  <div>Widen the period, or clear a filter or two.</div>
                </div>
              )}
            </div>

            <div className="sir-pager">
              <span className="sir-info">
                {list.length ? (
                  <>
                    Showing <b>{intg(Math.min(shown, list.length))}</b> of <b>{intg(list.length)}</b>
                  </>
                ) : (
                  "nothing to show"
                )}
              </span>
              {list.length > 0 && (
                <span className="sir-info">
                  {left > 0 ? `· ${BATCH} per batch, scroll for more` : "· all loaded"}
                </span>
              )}
              <div className="sir-mid">
                {left > 0 && (
                  <button
                    type="button"
                    className="sir-pgb"
                    onClick={() => setShown((s) => Math.min(s + BATCH, list.length))}
                  >
                    Load {Math.min(BATCH, left)} more
                  </button>
                )}
              </div>
            </div>
          </section>

          <footer className="sir-status">
            <span className="sir-sg"><span className="sir-kbd">F3</span>Search</span>
            <span className="sir-sg"><span className="sir-kbd">Enter</span>Apply</span>
            <span className="sir-sg"><span className="sir-kbd">Esc</span>Close panel</span>
            <span className="sir-sp" />
            <span className="sir-sg">
              {rows.length} of {intg(invoices.length)} invoices
              {loadMs !== null ? ` · loaded in ${loadMs} ms` : ""}
            </span>
          </footer>
        </div>
      </div>

      {/* ============ DRILL-DOWN ============ */}
      {di && (
        <>
          <div className={"sir-scrim" + (drawerOpen ? " show" : "")} onClick={closeDrawer} />
          <aside className={"sir-drawer" + (drawerOpen ? " show" : "")} aria-hidden={!drawerOpen}>
            <div className="sir-dhead">
              <span className="sir-av" style={{ "--h": pmHue(di.customer) }}>
                {pmInitials(di.customer)}
              </span>
              <div>
                <div className="sir-dt">{di.customer}</div>
                <div className="sir-ds">
                  {di.billNo} · {dmy(di.date)}
                </div>
              </div>
              <button type="button" className="sir-dclose" aria-label="Close" onClick={closeDrawer}>
                <X />
              </button>
            </div>
            <div className="sir-dbody">
              <div className="sir-dmeta">
                {[
                  ["Warehouse", di.warehouse],
                  ["Salesman", di.salesman || "—"],
                  ["Payment", di.payment || "—"],
                ].map(([k, v]) => (
                  <div className="sir-dm" key={k}>
                    <div className="sir-dk">{k}</div>
                    <div className="sir-dv">{v}</div>
                  </div>
                ))}
              </div>
              <div className="sir-secthead">Invoice lines</div>
              <table className="sir-lines">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="r">Qty</th>
                    <th className="r">Rate</th>
                    <th className="r">Disc</th>
                    <th className="r">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {drawerLines.length === 0 && (
                    <tr>
                      <td colSpan={5} className="sir-nolines">No lines on this invoice</td>
                    </tr>
                  )}
                  {drawerLines.map((l, i) => (
                    <tr key={l?.guid || i}>
                      <td>
                        <div className="sir-nm">{l?.product?.name || "Unknown Item"}</div>
                        <div className="sir-cd">
                          {[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}
                        </div>
                      </td>
                      <td className="r">{intg(l?.quantity)}</td>
                      <td className="r">{money(l?.price)}</td>
                      <td className="r">{num(l?.discAmt) ? money(l.discAmt) : "—"}</td>
                      <td className="r">{money(l?.netAmt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="sir-dtot">
                {[
                  ["Gross", money(di.total)],
                  ["Discount", di.discount ? "−" + money(di.discount) : "0.00"],
                  ["Additional", money(di.additional)],
                  ["VAT", money(di.tax)],
                  ["Round off", money(di.roundOff)],
                ].map(([l, v]) => (
                  <div className="sir-dtr" key={l}>
                    <span className="l">{l}</span>
                    <span className="v">{v}</span>
                  </div>
                ))}
              </div>
              <div className="sir-dnet">
                <span className="l">Net total</span>
                <span className="v">{money(di.net)}</span>
              </div>
            </div>
            <div className="sir-dfoot">
              <button type="button" className="sir-btn sir-btn-ghost" onClick={closeDrawer}>
                Close
              </button>
            </div>
          </aside>
        </>
      )}

      <div className={"sir-toast" + (toastMsg ? " show" : "")} role="status">
        <span className="ok">✓</span>
        <span>{toastMsg}</span>
      </div>
    </div>
  );
};

export default SalesInvoiceReport;
